import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { dateRange, stageMeta, outstandingTone, OUTSTANDING, waNumber } from '../lib/ui';
import { TempChip } from './TempChip';

// BUG 5 — per-trip visa status chip (shown on the traveller profile trip
// history, where `trip.viewerVisaStatus` is set for the viewed traveller).
const VISA_CHIP = {
  not_required: { label: 'Visa: Not required', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  applied: { label: 'Visa: Applied', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Visa: Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Visa: Rejected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  expired: { label: 'Visa: Expired', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

/**
 * Mobile trip card (Dashboard + Trips tabs). Strict 3-line format:
 *   L1: trip title + stage pill
 *   L2: destination · live temperature chip · dates
 *   L3: payment badge (Paid / Pending / Overdue)
 * No field is ever repeated (guest name never shown twice).
 * right: quick WhatsApp · tap → native trip detail (/m/trips/:id)
 */
export const TripCard = ({ trip }) => {
  const navigate = useNavigate();
  const stage = stageMeta(trip);
  const tone = outstandingTone(trip);
  const hasGuest = trip.guestName && trip.guestName !== '(Pending guest)';
  const title = hasGuest ? trip.guestName : (trip.destination || 'Untitled trip');
  const showDest = trip.destination && trip.destination !== title;
  const dates = dateRange(trip.travelStartDate, trip.travelEndDate);
  const wa = waNumber(trip.countryCode || '+91', trip.phone);
  const visa = VISA_CHIP[trip.viewerVisaStatus];

  const openWa = (e) => {
    e.stopPropagation();
    if (!trip.phone) return;
    window.open(`https://wa.me/${wa}`, '_blank', 'noopener');
  };

  return (
    <button
      type="button"
      onClick={() => navigate(`/m/trips/${trip.id}`)}
      data-testid={`m-trip-card-${trip.id}`}
      className="w-full text-left bg-white border border-[#E5E7EB] rounded-xl p-3.5 active:bg-[#F8F9FA]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {/* L1 — title + stage pill */}
          <div className="flex items-center gap-2">
            <p className="text-[15px] font-semibold text-[#111827] truncate">{title}</p>
            <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${stage.cls}`}>{stage.label}</span>
          </div>

          {/* L2 — destination · temp · dates */}
          <div className="flex items-center gap-2 mt-1 text-[13px] text-[#6B7280] min-w-0">
            {showDest && <span className="truncate min-w-0">{trip.destination}</span>}
            <TempChip destination={trip.destination} />
            {dates && <span className="shrink-0 ml-auto text-[12px]">{dates}</span>}
          </div>

          {/* L3 — payment badge + per-trip visa status (profile view) */}
          {(tone || visa) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {tone && (
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${OUTSTANDING[tone].cls}`} data-testid={`m-trip-payment-${trip.id}`}>
                  {OUTSTANDING[tone].label}
                </span>
              )}
              {visa && (
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${visa.cls}`} data-testid={`m-trip-visa-${trip.id}`}>
                  {visa.label}
                </span>
              )}
            </div>
          )}
        </div>

        {trip.phone && (
          <span
            role="button"
            tabIndex={0}
            onClick={openWa}
            onKeyDown={(e) => { if (e.key === 'Enter') openWa(e); }}
            data-testid={`m-trip-wa-${trip.id}`}
            aria-label="WhatsApp traveller"
            className="shrink-0 w-11 h-11 rounded-lg border border-[#E5E7EB] flex items-center justify-center text-[#0F6E56] active:bg-[#E7F1ED]"
          >
            <MessageCircle className="h-5 w-5" />
          </span>
        )}
      </div>
    </button>
  );
};

export default TripCard;
